import { Field, FieldProps, FormikProps } from 'formik';
import * as React from 'react';
import { OperationVariables, QueryResult, useMutation } from '@apollo/client';
import styled from 'styled-components';
// @ts-ignore
import { RadioGroup } from 'react-radio-buttons';
import {
  Button,
  FieldRow,
  StyledFormGroup,
  StyledFormSelectFormik,
  WrappedQuery,
  StyledFormikDatePicker,
  Spacer,
  StyledFormPhoneFormik,
  Box,
  RadioButton,
} from '../../../../components/shared';
import {
  ALL_COMMUNITY_TYPES,
  ALL_COUNTRIES,
  ALL_PROVINCES,
  ALL_LOCAL_MUNICIPALITIES,
  FILTERED_REASON_FOR_TERMINATIONS,
  FILTERED_STAKEHOLDER_ROLES,
  ALL_DISTRICT_MUNICIPALITIES,
  RESERVE_CASE_ID,
} from '../../../../graphql';
import { formatSelectValue, isWeb } from '../../../../utils';
import { Option, PeopleProfileInstitutionFormType, PeopleProfileOrganisationFormType } from '../../../../utils/types';
import { PersonDetailsFormType } from '../forms/PersonDetailsForm';
import { Label, Span } from '../../../../typography';
import { HouseholdMembersCard } from './HouseholdMembersCard';
import { CommunitiesCard } from './communitiesCard';
import { OrganisationsCard } from './organisationsCard';
import { useCaseIdFields } from '../../cases/useCaseIdFields';

type FormType = PersonDetailsFormType & {
  typeId: Option | undefined;
  portal: string;

  dateExited: Date | null;
  dateOfBirth: Date | null;
  districtId: Option | undefined;
  emisNumber: number | undefined;
  name: string;

  primaryInstitutionId: Option | undefined;
  institutionIds: Option[];
  associatedInstitutions: PeopleProfileInstitutionFormType[];
  associatedOrganisations: PeopleProfileOrganisationFormType[];
};

type Props = {
  formikProps: FormikProps<FormType>;
  submissionError?: string;
  userRoles?: any;
  householdIdString?: string | null;
};

const Title = styled.span`
  display: flex;
  font-size: 16px;
  font-weight: 600;
`;

const FieldsForCommunityMember: React.FC<Props> = props => {
  const { formikProps, submissionError } = props;
  const caseIdFields = useCaseIdFields();

  const [reserveCaseId] = useMutation(RESERVE_CASE_ID);
  const [generatingId, setGeneratingId] = React.useState(false);
  const [generateIdError, setGenerateIdError] = React.useState('');

  // Under automatic allocation the system owns these three fields, so they are never hand-edited:
  // a new person generates them, and an existing profile shows them locked. Manual allocation
  // leaves them exactly as they were — free text maintained by the caseworker.
  const hasCaseId = Boolean(formikProps.values.caseId);
  const showGenerateIdButton = caseIdFields.isAutomatic && !formikProps.values.id && !hasCaseId;
  const caseIdsAreReadOnly = caseIdFields.isAutomatic && hasCaseId;

  const generateCaseId = async () => {
    setGenerateIdError('');
    setGeneratingId(true);
    try {
      const { data } = await reserveCaseId({
        variables: {
          input: {
            householdId: props.householdIdString || undefined,
          },
        },
      });

      const reservation = data?.reservation;
      if (!reservation) {
        setGenerateIdError('Could not generate a Case ID. Please try again.');
        return;
      }

      formikProps.setFieldValue('caseId', reservation.caseId);
      formikProps.setFieldValue('householdId', reservation.householdId || '');
      formikProps.setFieldValue('householdCode', reservation.householdCode || '');
    } catch (error) {
      // Surfaces the server's own wording for the exhausted-pool and member-code exceptions.
      setGenerateIdError(
        (error as any)?.message?.replace('GraphQL error:', '').trim() || 'Could not generate a Case ID.'
      );
    } finally {
      setGeneratingId(false);
    }
  };
  const {
    values: { dateExited },
  } = formikProps;

  return (
    <React.Fragment>
      <Spacer spacing={20} />
      <Title>Next of Kin</Title>
      <Spacer spacing={10} />
      <FieldRow>
        <Field
          name={`nextOfKinName`}
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Next of Kin Name" mr={4} />
          )}
        />
        <Field
          name={`nextOfKinSurname`}
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Next of Kin Surname" mr={4} />
          )}
        />
        <Field
          name="nextOfKinContactNumber"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormPhoneFormik
              {...field}
              field={field}
              form={formikProps}
              defaultCountry="ZA"
              label="Next of Kin Contact Number"
              mr={4}
            />
          )}
        />
        <Field
          name="nextOfKinAlternateContactNumber"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormPhoneFormik
              {...field}
              field={field}
              form={formikProps}
              defaultCountry="ZA"
              label="Next of Kin Alternate Contact Number"
            />
          )}
        />
      </FieldRow>
      <Spacer spacing={20} />
      <FieldRow>
        <Field
          name="emailAddress"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Email Address" mr={4} />
          )}
        />
        <Field
          name="physicalAddress"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Physical Address" mr={4} />
          )}
        />
        <Field
          name="streetNo"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Street Number" mr={4} />
          )}
        />
        <Field
          name="streetName"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Street Name" />
          )}
        />
      </FieldRow>
      <FieldRow>
        <Field
          name="townshipVillageSuburb"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Suburb/Village/Township" mr={4} />
          )}
        />
        <Field
          name="townCity"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="City/Town" mr={4} />
          )}
        />
        <Field
          name="postalCode"
          render={({ field }: FieldProps<FormType>) => (
            <StyledFormGroup {...field} field={field} form={formikProps} label="Postal Code" mr={4} />
          )}
        />
        <WrappedQuery
          query={FILTERED_STAKEHOLDER_ROLES}
          variables={{
            input: {
              typeId: formikProps.values.typeId && formikProps.values.typeId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="roleId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Role"
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.filteredStakeholderRoles &&
                          data.filteredStakeholderRoles.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>
      <FieldRow>
        {/* Under automatic allocation a new person has no IDs until they are generated, so the row
            offers the button instead of empty inputs. Once generated — and on any profile that
            already has IDs — the fields are shown but locked, because the system owns them. */}
        {showGenerateIdButton ? (
          <Box flex="0 0 auto" mr={4}>
            <Label>Case ID</Label>
            <div>
              <Button
                type="button"
                text="Generate ID"
                width="auto"
                p={0}
                isLoading={generatingId}
                onClick={generateCaseId}
              />
            </div>
            {generateIdError ? (
              <Span width="100%" color="error" mt={2}>
                {generateIdError}
              </Span>
            ) : null}
          </Box>
        ) : (
          <React.Fragment>
            <Field
              name="caseId"
              render={({ field }: FieldProps<FormType>) => (
                <React.Fragment>
                  <StyledFormGroup
                    {...field}
                    field={field}
                    form={formikProps}
                    label="Case ID"
                    mr={4}
                    disabled={caseIdsAreReadOnly}
                  />
                  {submissionError && (
                    <Span width="100%" color="error" mt={2}>
                      {submissionError}
                    </Span>
                  )}
                </React.Fragment>
              )}
            />
            {caseIdFields.householdIdEnabled && (
              <Field
                name="householdId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormGroup
                    {...field}
                    field={field}
                    form={formikProps}
                    label="Household ID"
                    mr={4}
                    disabled={caseIdsAreReadOnly || !!(props.householdIdString && !formikProps.values.id)}
                  />
                )}
              />
            )}
            {caseIdFields.householdCodeEnabled && (
              <Field
                name="householdCode"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormGroup
                    {...field}
                    field={field}
                    form={formikProps}
                    label="Household Code"
                    disabled={caseIdsAreReadOnly}
                    onChange={(e: any) => {
                      const next = (e.target.value || '')
                        .toUpperCase()
                        .replace(/[^A-Z]/g, '')
                        .slice(0, 1);
                      formikProps.setFieldValue('householdCode', next);
                    }}
                  />
                )}
              />
            )}
          </React.Fragment>
        )}
      </FieldRow>
      <Spacer spacing={10} />
      <FieldRow>
        <FieldRow>
          <Field
            name="linkedToCommunity"
            render={({ field }: FieldProps<FormType>) => (
              <Box {...formikProps} flexDirection={!isWeb ? 'row' : 'column'}>
                <Label htmlFor="linkedToCommunity">Linked to a Community</Label>
                <RadioGroup
                  value={formikProps.values.linkedToCommunity}
                  onChange={(value: string) => {
                    formikProps.setFieldValue('linkedToCommunity', value);
                  }}
                  horizontal={true}
                >
                  <RadioButton value="NO">No</RadioButton>
                  <RadioButton value="YES">Yes</RadioButton>
                </RadioGroup>
              </Box>
            )}
            mr={4}
          />
        </FieldRow>
        <FieldRow>
          <Field
            name="linkedToOrganisation"
            render={({ field }: FieldProps<FormType>) => (
              <Box {...formikProps} flexDirection={!isWeb ? 'row' : 'column'}>
                <Label htmlFor="linkedToOrganisation">Linked to an Organisation</Label>
                <RadioGroup
                  value={formikProps.values.linkedToOrganisation}
                  onChange={(value: string) => {
                    formikProps.setFieldValue('linkedToOrganisation', value);
                  }}
                  horizontal={true}
                >
                  <RadioButton value="NO">No</RadioButton>
                  <RadioButton value="YES">Yes</RadioButton>
                </RadioGroup>
              </Box>
            )}
          />
        </FieldRow>
      </FieldRow>
      <Spacer spacing={10} />
      <FieldRow>
        <WrappedQuery query={ALL_COMMUNITY_TYPES} overrideStates={true}>
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="communityTypeId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Community Type"
                    form={formikProps}
                    isDisabled={true}
                    options={
                      loading || error
                        ? []
                        : data.communityTypes.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        <WrappedQuery query={ALL_COUNTRIES} variables={{ input: {} }} overrideStates={true}>
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="countryId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Country"
                    isDisabled={true}
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.countries.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        <WrappedQuery query={ALL_PROVINCES} variables={{ input: {} }} overrideStates={true}>
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="provinceId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Province"
                    isDisabled={true}
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.provinces.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>
      <FieldRow>
        <WrappedQuery
          query={ALL_DISTRICT_MUNICIPALITIES}
          variables={{
            input: {
              provinceId: formikProps.values.provinceId?.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="districtMunicipalityId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="District Municipality"
                    isDisabled={true}
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.municipalities.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                    mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
        <WrappedQuery
          query={ALL_LOCAL_MUNICIPALITIES}
          variables={{
            input: {
              districtMunicipalityId: formikProps.values.districtMunicipalityId?.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="localMunicipalityId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Local Municipality"
                    isDisabled={true}
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.municipalities &&
                          data.municipalities.map((item: any) =>
                            formatSelectValue({
                              label: item.localMunicipalityShorthand,
                              value: item.id,
                            })
                          )
                    }
                    // mr={4}
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>
      <FieldRow>
        <Field
          render={() => (
            <StyledFormikDatePicker
              isSingleDate={true}
              form={formikProps}
              label="Date Exited / Termination"
              readOnly={true}
              dateLabel="dateExited"
              date={dateExited || null}
              mr={4}
            />
          )}
        />
        <WrappedQuery
          query={FILTERED_REASON_FOR_TERMINATIONS}
          variables={{
            input: {
              typeId: formikProps.values.typeId && formikProps.values.typeId.value,
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
            return (
              <Field
                name="terminationId"
                render={({ field }: FieldProps<FormType>) => (
                  <StyledFormSelectFormik
                    {...field}
                    field={field}
                    label="Reason for Exit"
                    isDisabled={true}
                    form={formikProps}
                    options={
                      loading || error
                        ? []
                        : data.filteredReasonForTerminations &&
                          data.filteredReasonForTerminations.map((item: any) =>
                            formatSelectValue({
                              label: item.name,
                              value: item.id,
                            })
                          )
                    }
                  />
                )}
              />
            );
          }}
        </WrappedQuery>
      </FieldRow>

      <HouseholdMembersCard
        householdId={formikProps.values.householdId}
        personId={formikProps.values.id}
        privateToOrganisation={formikProps.values.privateToOrganisation}
      />
      {formikProps.values.linkedToCommunity !== 'NO' && (
        <CommunitiesCard formikProps={formikProps} viewMode={false} currentInstitutionsOnly={true} />
      )}
      {formikProps.values.linkedToOrganisation !== 'NO' && (
        <OrganisationsCard formikProps={formikProps} currentOrganisationsOnly={formikProps.values.portal !== 'admin'} />
      )}
    </React.Fragment>
  );
};

export default FieldsForCommunityMember;
